import { useState } from "react";
import { useTranslation } from "react-i18next";

import ITournamentGroupLeaderboard from "../../../Interfaces/TournamentGroupLeaderboard";
import ITournament from "../../../Interfaces/Tournament";
import ITournamentGroup from "../../../Interfaces/TournamentGroup";
import Modal from "../../../UiLibrary/Modal";
import IMatchTournamentSeed from "../../../Interfaces/MatchTournamentSeed";
import MatchesTab from "./MatchesTab";
import TournamentGroupDetails from "../../../Modals/TournamentGroupDetails/TournamentGroupDetails";
import TournamentService from "../../../../services/TournamentService";
import { toast } from "react-toastify";
import UserProfileWithDetailsModal from "../../../UiLibrary/User/UserProfileWithDetailsModal";
import MatchTeamName from "../../../UiLibrary/MatchComponents/MatchTeamName";
import EditTournamentGroupMondal from "../../../Modals/EditTournamentGroupModal/EditTournamentGroupMondal";

type Props = {
  tournament: ITournament;
  onSetMatch: (matchSeed: IMatchTournamentSeed) => void;
  isAdmin?: boolean;
  loadTournament: (tournament: ITournament | undefined) => void;
  showMatchDetailModal?: boolean;
};

// 🔽 Nuova funzione per calcolare la leaderboard dai results
const generateLeaderboardFromResults = (
  group: ITournamentGroup
): ITournamentGroupLeaderboard[] => {
  const standings: Record<string, ITournamentGroupLeaderboard> = {};
  const results = group.results || [];

  for (const result of results) {
    const [teamA, teamB] = Object.keys(result.scores);
    const scoreA = result.scores[teamA];
    const scoreB = result.scores[teamB];

    const ensure = (id: string) => {
      if (!standings[id]) {
        standings[id] = {
          teamId: id,
          win: 0,
          draws: 0,
          defeats: 0,
          points: 0,
          goalsFor: 0,
          goalsAgainst: 0,
          goalDifference: 0,
        };
      }
    };

    ensure(teamA);
    ensure(teamB);

    // Aggiungiamo gol fatti e subiti
    standings[teamA].goalsFor += scoreA;
    standings[teamA].goalsAgainst += scoreB;

    standings[teamB].goalsFor += scoreB;
    standings[teamB].goalsAgainst += scoreA;

    // Assegniamo punti e risultati
    if (scoreA > scoreB) {
      standings[teamA].win += 1;
      standings[teamA].points += group.pointSystem.pointsPerWin;

      standings[teamB].defeats += 1;
      standings[teamB].points += group.pointSystem.pointsPerLose;
    } else if (scoreA < scoreB) {
      standings[teamB].win += 1;
      standings[teamB].points += group.pointSystem.pointsPerWin;

      standings[teamA].defeats += 1;
      standings[teamA].points += group.pointSystem.pointsPerLose;
    } else {
      standings[teamA].draws += 1;
      standings[teamB].draws += 1;

      standings[teamA].points += group.pointSystem.pointsPerDraw;
      standings[teamB].points += group.pointSystem.pointsPerDraw;
    }
  }

  // Includiamo tutte le squadre anche senza match
  for (const teamId of group.teams) {
    if (!standings[teamId]) {
      standings[teamId] = {
        teamId,
        win: 0,
        draws: 0,
        defeats: 0,
        points: 0,
        goalsFor: 0,
        goalsAgainst: 0,
        goalDifference: 0,
      };
    }
  }

  // Calcolo della differenza reti
  for (const team of Object.values(standings)) {
    team.goalDifference = team.goalsFor - team.goalsAgainst;
  }

  // Ordinamento finale: punti > diff reti > gol fatti
  return Object.values(standings).sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (b.goalDifference !== a.goalDifference)
      return b.goalDifference - a.goalDifference;
    return b.goalsFor - a.goalsFor;
  });
};

const GroupsTab = (props: Props) => {
  const { tournament, isAdmin, showMatchDetailModal } = props;
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [showMatchesModal, setShowMatchesModal] = useState<{
    show: boolean;
    group?: ITournamentGroup;
    matches?: IMatchTournamentSeed[];
  }>({ show: false });

  const [showEditGroupModal, setShowEditGroupModal] = useState<{
    show: boolean;
    group?: ITournamentGroup;
  }>({ show: false });

  const showDetailsModal = (group: ITournamentGroup) => {
    const matchesByGroup = tournament.matches.filter(
      (match: IMatchTournamentSeed) => match.groupId === group.id
    );
    setShowMatchesModal({ show: true, group: group, matches: matchesByGroup });
  };

  const editGroup = (e: any, group: ITournamentGroup) => {
    e.preventDefault();
    e.stopPropagation();
    setShowEditGroupModal({ show: true, group: group });
  };

  const saveGroup = (group: {
    name: string;
    index?: number;
    pointSystem: {
      pointsPerWin: number;
      pointsPerLose: number;
      pointsPerDraw: number;
    };
  }) => {
    if (!showEditGroupModal.group) return;
    setLoading(true);
    TournamentService.editGroup(tournament.id, showEditGroupModal.group.id, {
      name: group.name,
      index: group.index,
      pointSystem: {
        pointsPerWin: group.pointSystem.pointsPerWin,
        pointsPerLose: group.pointSystem.pointsPerLose,
        pointsPerDraw: group.pointSystem.pointsPerDraw,
      },
    })
      .then((RTournament) => {
        setLoading(false);
        toast.success(t("toast.leaderboardUpdatedSuccess"));

        TournamentService.reloadGroupLeaderboard(
          tournament.id,
          showEditGroupModal.group!.id
        )
          .then((response) => {
            setShowEditGroupModal({ show: false });
            response.id = response._id;
            props.loadTournament(response);
          })
          .catch((error) => {
            setLoading(false);
            toast.error(error?.response?.data?.message);
          });
      })
      .catch((error) => {
        setLoading(false);
        toast.error(error?.response?.data?.message);
      });
  };

  return (
    <>
      <div className="container">
        <div className="space-divider-20"></div>
        <div className="columns">
          <div className="column col-12">
            <h5 className="skp-text-primary">{t("tabs.groups")}</h5>
          </div>
        </div>
        <div className="space-divider-20"></div>
        {!showMatchesModal.show && (
          <div className="columns">
            {(tournament.groups || [])
              .sort((a, b) => {
                const indexA =
                  typeof a.index === "number"
                    ? a.index
                    : Number.MAX_SAFE_INTEGER;
                const indexB =
                  typeof b.index === "number"
                    ? b.index
                    : Number.MAX_SAFE_INTEGER;
                return indexA - indexB;
              })
              .map((group: ITournamentGroup) => (
                <div className="column col-12" key={group?.id}>
                  <div className="skp-card skp-card-hover">
                    <div className="accordion pt-2 pl-2 pr-2">
                      <input
                        type="checkbox"
                        id={`accordion-group-${group?.id}`}
                        name={`group-${group?.id}`}
                        hidden
                      />
                      <label
                        className="accordion-header"
                        htmlFor={`accordion-group-${group?.id}`}
                      >
                        <div className="columns skp-text-primary pt-2">
                          <div className="column col-10">
                            <h5>{group?.name}</h5>
                          </div>
                          <div className="column col-2 text-right">
                            {isAdmin && (
                              <span
                                className="icon-holder p-2 skp-link-hover"
                                onClick={(e) => editGroup(e, group)}
                              >
                                <i className="las la-pen"></i>
                              </span>
                            )}
                            <span className="icon-holder p-2">
                              <i className="las la-angle-right"></i>
                            </span>
                          </div>
                        </div>
                      </label>
                      <div className="accordion-body">
                        <dl
                          className="skp-text-light"
                          style={{ marginTop: "-20px" }}
                        >
                          <dd
                            className="columns skp-text-small"
                            style={{ padding: "10px" }}
                          >
                            <div className="column col-5">
                              <span
                                className="skp-link-hover"
                                onClick={() => showDetailsModal(group)}
                              >
                                {t("common.showDetails")}
                              </span>
                            </div>
                            <div className="column col-1 text-center">
                              {t("common.gamesPlayed")}
                            </div>
                            <div className="column col-1 text-center">
                              {t("common.wins")}
                            </div>
                            <div className="column col-1 text-center">
                              {t("common.draws")}
                            </div>
                            <div className="column col-1 text-center">
                              {t("common.defeats")}
                            </div>
                            <div className="column col-1 text-center">
                              {t("common.goalsFor")} {/* ⚽ Gol fatti */}
                            </div>
                            <div className="column col-1 text-center">
                              {t("common.goalsAgainst")} {/* 🥅 Gol subiti */}
                            </div>
                            <div className="column col-1 text-center">
                              <strong>{t("common.points")}</strong>
                            </div>
                          </dd>
                        </dl>
                        <dl className="bordered-list skp-text-light">
                          {generateLeaderboardFromResults(group)
                            .sort((a, b) => b.points - a.points)
                            .map((team) => {
                              const gamesPlayed =
                                team.win + team.draws + team.defeats;
                              return (
                                <dd
                                  className="columns skp-text-small"
                                  key={team.teamId + "-" + group?.id}
                                >
                                  <div className="column col-5 skp-text-default">
                                    {tournament.playerType === 0 && (
                                      <UserProfileWithDetailsModal
                                        userId={team.teamId}
                                        tournament={tournament}
                                      />
                                    )}
                                    {tournament.playerType === 1 && (
                                      <MatchTeamName
                                        tournament={tournament}
                                        teamId={team.teamId}
                                        hideTeamLabel
                                      />
                                    )}
                                  </div>
                                  <div className="column col-1 text-center">
                                    {team.win + team.draws + team.defeats}
                                  </div>
                                  <div className="column col-1 text-center">
                                    {team.win}
                                  </div>
                                  <div className="column col-1 text-center">
                                    {team.draws}
                                  </div>
                                  <div className="column col-1 text-center">
                                    {team.defeats}
                                  </div>
                                  <div className="column col-1 text-center">
                                    {team.goalsFor}
                                  </div>
                                  <div className="column col-1 text-center">
                                    {team.goalsAgainst}
                                  </div>
                                  <div className="column col-1 text-center">
                                    <strong>{team.points}</strong>
                                  </div>
                                </dd>
                              );
                            })}
                        </dl>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>

      <Modal
        title={`${showMatchesModal.group?.name}`}
        show={showMatchesModal.show}
        onClose={() => setShowMatchesModal({ show: false })}
      >
        <>
          <TournamentGroupDetails />
          {showMatchesModal.matches && !showMatchDetailModal && (
            <MatchesTab
              matches={showMatchesModal.matches}
              tournament={tournament}
              onLogin={() => {}}
              onSetMatch={(match) => props.onSetMatch(match)}
            />
          )}
        </>
      </Modal>

      <Modal
        title={`Edit ${showEditGroupModal.group?.name}`}
        show={showEditGroupModal.show}
        size="small"
        onClose={() => setShowEditGroupModal({ show: false })}
        loading={loading}
      >
        <>
          {showEditGroupModal.group && (
            <EditTournamentGroupMondal
              group={showEditGroupModal.group}
              onSubmit={saveGroup}
              loading={loading}
            />
          )}
        </>
      </Modal>
    </>
  );
};

export default GroupsTab;
